<template>
  <div class="us-input us-input--checkbox" data-test="us-input-checkbox">
    <component
      :is="htmlHeadingTag"
      v-if="heading"
      class="display--d2--semibold input-heading-wrapper"
    >
      {{ heading }}
    </component>
    <slot name="heading" />
    <p v-if="description" class="body--p1--regular input-description-wrapper">
      {{ description }}
    </p>
    <slot name="description" />

    <div class="input-control-wrapper">
      <label
        :class="['input-label', disabled ? 'input-label--disabled' : '']"
        :for="inputId"
      >
        <input
          :id="inputId"
          :value="modelValue"
          class="checkbox-input"
          type="checkbox"
          :name="name"
          :disabled="disabled"
          :checked="modelValue"
          @input="$emit('update:modelValue', $event.target.checked)"
        />
        <span data-select-label class="label body--p1--regular">
          {{ label }}
        </span>
        <slot name="label-slot" />
      </label>
      <div v-if="error" class="error body--p2--regular">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'USInputCheckbox',
  props: {
    modelValue: {
      type: [Boolean, String],
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    inputId: {
      type: String,
      required: true
    },
    name: {
      type: String,
      required: true
    },
    heading: {
      type: String,
      default: ''
    },
    headingLevel: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6>,
      default: 1
    },
    description: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  setup(props) {
    const htmlHeadingTag = computed(() => `h${props.headingLevel}`)
    return { htmlHeadingTag }
  }
})
</script>

<style lang="scss" scoped>
@use '@base/@motiv/fe-patterns/styles/checkbox' as *;

@use '@base/styles/v1.0/scss/foundations/breakpoint' as bp;

.input-label {
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-flow: row nowrap;
  user-select: none;
}
.error {
  color: var(--color--semantic--error-2);
  padding-top: var(--spacing--8);
}

@include checkbox(
  $checkbox-selector: '.input-label',
  $gutter-between-checkbox-label: var(--spacing--8),
  $transition-speed: var(--animation-speed-medium),
  $background-color: var(--color--secondary-c2),
  $hover-background-color: var(--color--secondary-c2),
  $breakpoint: bp.$breakpoint--tablet,
  $checkbox-size-sm: 19px,
  $checkbox-size-lg: 24px,
  $checked-background-color: var(--color--primary-b1),
  $checkmark-size-sm: 10px,
  $checkmark-size-lg: 14px,
  $checkmark-color: var(--color--neutral-a5),
  $focus-color: transparent
);

input[type='checkbox'] {
  border-radius: 4px;
  border: 2px solid transparent;

  &:disabled {
    background-color: var(--color--neutral-a4);
  }

  &:hover:not(:disabled) {
    border: var(--line-weight--heavy) solid var(--color--primary-b1) !important;
  }
}

.input-heading-wrapper {
  margin-bottom: var(--spacing--9);
}

.input-description-wrapper {
  margin-bottom: var(--spacing--8);
}
</style>
